import React, { useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { AuthProvider } from "./context/AuthContext";
import { SettingsProvider } from "./context/SettingsContext";

import ProtectedRoute from "./components/common/ProtectedRoute";
import Sidebar from "./components/layout/Sidebar";
import Topbar from "./components/layout/Topbar";

import LoginPage from "./components/pages/LoginPage";
import Dashboard from "./components/pages/Dashboard";
import SiteSettingsPage from "./components/pages/SiteSettingsPage";
import ChangePasswordPage from "./components/pages/ChangePasswordPage";
import ServiceListPage from "./components/pages/ServiceListPage";
import AddServicePage from "./components/pages/AddServicePage";
import LogoPage from "./components/pages/LogoPage";
import BlogListPage from "./components/pages/BlogListPage";
import AddBlogPage from "./components/pages/AddBlogPage";
import FaqListPage from "./components/pages/FaqListPage";
import AddFaqPage from "./components/pages/AddFaqPage";
import GaleriTagListPage from "./components/pages/gallery/GaleriTagListPage";
import AddGaleryPage from "./components/pages/gallery/AddGaleryPage";
import GaleriListPage from "./components/pages/gallery/GaleriListPage";
import ReferenceManager from "./components/pages/reference/ReferenceManager";
import ReferenceList from "./components/pages/reference/ReferenceList";
import BannerListPage from "./components/pages/banner/BannerListPage";
import AddBannerPage from "./components/pages/banner/AddBannerPage";
import TestimonialListPage from "./components/pages/testimonial/TestimonialListPage";
import AddTestimonialPage from "./components/pages/testimonial/AddTestimonialPage";
import ServiceCategoryListPage from "./components/pages/serviceCategory/ServiceCategoryListPage";
import AddServiceCategoryPage from "./components/pages/serviceCategory/AddServiceCategoryPage";
import EmployeeListPage from "./components/pages/employee/EmployeeListPage";
import AddEmployeePage from "./components/pages/employee/AddEmployeePage";
import ProductListPage from "./components/pages/product/ProductListPage";
import AddProductPage from "./components/pages/product/AddProductPage";
import ProductCategoryListPage from "./components/pages/product/ProductCategoryListPage";
import AddProductCategoryPage from "./components/pages/product/AddProductCategoryPage";
import ContractedBankList from "./components/pages/contractedBank/ContractedBankList";
import ContractedBankManager from "./components/pages/contractedBank/ContractedBankManager";
import ContractedInstitutionList from "./components/pages/contractedInstitution/ContractedInstitutionList";
import ContractedInstitutionManager from "./components/pages/contractedInstitution/ContractedInstitutionManager";
import ModulePermissionsPage from "./components/pages/ModulePermissionsPage";
import AppointmentListPage from "./components/pages/appointment/AppointmentListPage";
import AvailabilityPage from "./components/pages/appointment/AvailabilityPage";

import "./styles/index.css";

function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(true);

  return (
    <div
      className={`app-layout ${sidebarOpen ? "sidebar-open" : "sidebar-closed"}`}
    >
      <Sidebar
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
      />
      <div className="main-content">
        <Topbar onToggleSidebar={() => setSidebarOpen(!sidebarOpen)} />
        <main className="page-wrapper">{children}</main>
      </div>
    </div>
  );
}

const protectedPage = (Component) => (
  <ProtectedRoute>
    <Layout>
      <Component />
    </Layout>
  </ProtectedRoute>
);

export default function App() {
  return (
    <AuthProvider>
      <SettingsProvider>
        <Router>
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 3000,
              style: {
                background: "#1e293b",
                color: "#f8fafc",
                borderRadius: "12px",
                fontSize: "14px",
              },
              success: { iconTheme: { primary: "#10b981", secondary: "#fff" } },
              error: { iconTheme: { primary: "#ef4444", secondary: "#fff" } },
            }}
          />
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/" element={protectedPage(Dashboard)} />
            <Route path="/settings" element={protectedPage(SiteSettingsPage)} />
            <Route
              path="/change-password"
              element={protectedPage(ChangePasswordPage)}
            />
            <Route
              path="/eminegunay"
              element={protectedPage(ModulePermissionsPage)}
            />
            <Route
              path="/service-list"
              element={protectedPage(ServiceListPage)}
            />
            <Route
              path="/add-service"
              element={protectedPage(AddServicePage)}
            />
            <Route
              path="/edit-service/:id"
              element={protectedPage(AddServicePage)}
            />
            <Route path="/logo" element={protectedPage(LogoPage)} />
            <Route path="/blog-list" element={protectedPage(BlogListPage)} />
            <Route path="/blog-add" element={protectedPage(AddBlogPage)} />
            <Route path="/blog-add/:id" element={protectedPage(AddBlogPage)} />
            <Route path="/faq-list" element={protectedPage(FaqListPage)} />
            <Route path="/faq-add" element={protectedPage(AddFaqPage)} />
            <Route
              path="/galeri-tag-list"
              element={protectedPage(GaleriTagListPage)}
            />
            <Route
              path="/upload-galeri"
              element={protectedPage(AddGaleryPage)}
            />
            <Route
              path="/galeri-list"
              element={protectedPage(GaleriListPage)}
            />
            <Route
              path="/add-reference"
              element={protectedPage(ReferenceManager)}
            />
            <Route
              path="/reference-list"
              element={protectedPage(ReferenceList)}
            />
            <Route
              path="/banner-list"
              element={protectedPage(BannerListPage)}
            />
            <Route path="/add-banner" element={protectedPage(AddBannerPage)} />
            <Route
              path="/edit-banner/:id"
              element={protectedPage(AddBannerPage)}
            />
            <Route
              path="/testimonial-list"
              element={protectedPage(TestimonialListPage)}
            />
            <Route
              path="/add-testimonial"
              element={protectedPage(AddTestimonialPage)}
            />
            <Route
              path="/edit-testimonial/:id"
              element={protectedPage(AddTestimonialPage)}
            />
            <Route
              path="/service-category-list"
              element={protectedPage(ServiceCategoryListPage)}
            />
            <Route
              path="/add-service-category"
              element={protectedPage(AddServiceCategoryPage)}
            />
            <Route
              path="/edit-service-category/:id"
              element={protectedPage(AddServiceCategoryPage)}
            />
            <Route
              path="/employee-list"
              element={protectedPage(EmployeeListPage)}
            />
            <Route
              path="/add-employee"
              element={protectedPage(AddEmployeePage)}
            />
            <Route
              path="/edit-employee/:slug"
              element={protectedPage(AddEmployeePage)}
            />
            <Route
              path="/product-list"
              element={protectedPage(ProductListPage)}
            />
            <Route
              path="/add-product"
              element={protectedPage(AddProductPage)}
            />
            <Route
              path="/edit-product/:id"
              element={protectedPage(AddProductPage)}
            />
            <Route
              path="/product-category-list"
              element={protectedPage(ProductCategoryListPage)}
            />
            <Route
              path="/add-product-category"
              element={protectedPage(AddProductCategoryPage)}
            />
            <Route
              path="/edit-product-category/:id"
              element={protectedPage(AddProductCategoryPage)}
            />
            <Route
              path="/contracted-bank-list"
              element={protectedPage(ContractedBankList)}
            />
            <Route
              path="/add-contracted-bank"
              element={protectedPage(ContractedBankManager)}
            />
            <Route
              path="/edit-contracted-bank/:id"
              element={protectedPage(ContractedBankManager)}
            />
            <Route
              path="/contracted-institution-list"
              element={protectedPage(ContractedInstitutionList)}
            />
            <Route
              path="/add-contracted-institution"
              element={protectedPage(ContractedInstitutionManager)}
            />
            <Route
              path="/edit-contracted-institution/:id"
              element={protectedPage(ContractedInstitutionManager)}
            />
            <Route
              path="/appointment-list"
              element={protectedPage(AppointmentListPage)}
            />
            <Route
              path="/availability"
              element={protectedPage(AvailabilityPage)}
            />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </Router>
      </SettingsProvider>
    </AuthProvider>
  );
}
